import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import * as SimpleBiReportsApi from './simpleBiReportsApi';

const BUSINESS_TIMEZONE = 'Europe/Bucharest';
const AUTO_REFRESH_MS = 60000;
const DELIVERY_STATUSES = new Set([0, 1, 2, 3, 4, 5, 6, 9]);
const DELIVERY_STATUS_OPTIONS = [
  { value: '0', label: 'Not applicable' },
  { value: '1', label: 'Registered' },
  { value: '2', label: 'Assigned' },
  { value: '3', label: 'Picked up' },
  { value: '4', label: 'On the way' },
  { value: '5', label: 'Delivered' },
  { value: '6', label: 'Failed' },
  { value: '9', label: 'Undefined' }
];
const isIsoDate = value => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''));

export const getBusinessDate = (value = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(value).reduce((result, part) => ({
    ...result,
    [part.type]: part.value
  }), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

export const shiftIsoDate = (value, days) => {
  const [year, month, day] = String(value).split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return [
    shifted.getUTCFullYear(),
    String(shifted.getUTCMonth() + 1).padStart(2, '0'),
    String(shifted.getUTCDate()).padStart(2, '0')
  ].join('-');
};

const formatDateTime = value => {
  if (!value) return 'Not reported';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Not reported';
  return new Intl.DateTimeFormat('ro-RO', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).format(parsed);
};

const getPhoneLink = value => {
  const phone = String(value || '').trim();
  return /^\+[1-9][0-9]{6,14}$/.test(phone) ? `tel:${phone}` : '';
};

const getTrackingLink = value => {
  try {
    const parsed = new URL(String(value || ''));
    const hostname = parsed.hostname.toLowerCase();
    if (
      parsed.protocol !== 'https:' ||
      parsed.username ||
      parsed.password ||
      (parsed.port && parsed.port !== '443') ||
      (hostname !== 'glovoapp.com' && !hostname.endsWith('.glovoapp.com'))
    ) {
      return '';
    }
    return parsed.toString();
  } catch {
    return '';
  }
};

const errorMessage = (error, subject = 'Delivery orders') => {
  if (error?.status === 401) return 'Your session expired. Sign in again.';
  if (error?.status === 403) return 'You do not have permission to view this location.';
  if (error?.status === 404) return 'The requested report is no longer available.';
  if (error?.status === 400) return 'The selected delivery filters are invalid.';
  if (error?.message === 'Invalid delivery orders response.') {
    return 'The delivery report response is incomplete. Retry to load it again.';
  }
  if (error?.message === 'Invalid locations response.') {
    return 'The available locations response is incomplete. Retry to load it again.';
  }
  return `${subject} could not be loaded. Check your connection and retry.`;
};

const normalizeLocations = data => {
  const rows = Array.isArray(data) ? data : data?.locations;
  if (!Array.isArray(rows) || !rows.every(row => (
    row && row.iref !== null && row.iref !== undefined && typeof row.name === 'string'
  ))) {
    throw new Error('Invalid locations response.');
  }
  return rows.map(row => ({ iref: String(row.iref), name: row.name }));
};

const validDateTime = value => typeof value === 'string' && Number.isFinite(Date.parse(value));
const formatRemaining = milliseconds => {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

const validDeliveryOrder = order => (
  order &&
  order.iref !== null && order.iref !== undefined &&
  typeof order.code === 'string' &&
  order.location &&
  order.location.iref !== null && order.location.iref !== undefined &&
  typeof order.location.name === 'string' &&
  validDateTime(order.createdOn) &&
  validDateTime(order.scheduledOn) &&
  /^\d{4}-\d{2}-\d{2}$/.test(order.promisedDate) &&
  Number.isInteger(order.orderStatus) &&
  DELIVERY_STATUSES.has(order.deliveryStatus) &&
  Number.isFinite(Number(order.totalAmount)) &&
  Number.isInteger(order.numberOfLines) &&
  (order.contactName == null || typeof order.contactName === 'string') &&
  (order.contactPhone == null || typeof order.contactPhone === 'string') &&
  (order.courierDispatch === null || (
    typeof order.courierDispatch === 'object' &&
    Number.isInteger(order.courierDispatch.generation) &&
    (order.courierDispatch.cancellationReason == null || typeof order.courierDispatch.cancellationReason === 'string') &&
    (order.courierDispatch.cancellationReasonLabel == null || typeof order.courierDispatch.cancellationReasonLabel === 'string') &&
    (order.courierDispatch.cancellationSource == null || typeof order.courierDispatch.cancellationSource === 'string')
  )) &&
  order.courierRefresh &&
  typeof order.courierRefresh.eligible === 'boolean' &&
  (order.courierRefresh.nextAllowedOn === null || validDateTime(order.courierRefresh.nextAllowedOn))
);

const validDeliveryResponse = (data, selectedDate) => (
  data &&
  data.date === selectedDate &&
  data.dateBasis === 'promisedDate' &&
  data.timezone === BUSINESS_TIMEZONE &&
  Number.isInteger(data.count) &&
  Array.isArray(data.results) &&
  data.count === data.results.length &&
  data.results.every(validDeliveryOrder)
);

const getDeliveryStatusColor = status => {
  const value = Number(status);
  if (value === 5) return 'success';
  if (value === 6) return 'error';
  if (value === 3 || value === 4) return 'info';
  if (value === 1 || value === 2) return 'warning';
  return 'default';
};

const getShortOrderNumber = (code, iref) => {
  const trailingDigits = String(code || '').match(/(\d+)$/)?.[1];
  return trailingDigits ? trailingDigits.slice(-4) : String(iref ?? '—');
};

const InfoItem = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>
      {label}
    </Typography>
    <Typography component="div" sx={{ color: '#111827', fontSize: '0.78rem', overflowWrap: 'anywhere' }}>
      {children ?? '—'}
    </Typography>
  </Box>
);

const CourierDetails = ({ dispatch }) => {
  if (!dispatch) {
    return <Alert severity="info" sx={{ mt: 1 }}>No managed courier record is stored for this order.</Alert>;
  }
  const phoneLink = getPhoneLink(dispatch.courierPhone);
  const trackingLink = getTrackingLink(dispatch.trackingLink);
  const isCancelled = dispatch.providerStatus === 'CANCELLED' || dispatch.status === 'CANCELLED';
  return (
    <>
      {dispatch.errorCode === 'CANCEL_AFTER_PICKUP_CONFLICT' && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Glovo reported this courier attempt cancelled, but Kalopsia recorded pickup evidence. Confirm with the customer what was received.
        </Alert>
      )}
      <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.1 }}>
        <InfoItem label="Dispatch status">{dispatch.status || 'Not reported'}</InfoItem>
        <InfoItem label="Generation">{dispatch.generation}</InfoItem>
        <InfoItem label="Last update">{formatDateTime(dispatch.updatedOn)}</InfoItem>
        {dispatch.providerStatus && <InfoItem label="Glovo status">{dispatch.providerStatus}</InfoItem>}
        {isCancelled && (
          <InfoItem label="Glovo reported cancellation reason">
            {dispatch.cancellationReasonLabel || dispatch.cancellationReason || 'Not available in stored details'}
          </InfoItem>
        )}
        {isCancelled && dispatch.cancellationSource && (
          <InfoItem label="Glovo reported cancellation source">{dispatch.cancellationSource}</InfoItem>
        )}
        {dispatch.providerOrderId && <InfoItem label="Glovo order ID">{dispatch.providerOrderId}</InfoItem>}
        {dispatch.courierName && <InfoItem label="Courier name">{dispatch.courierName}</InfoItem>}
        {dispatch.courierPhone && (
          <InfoItem label="Courier phone">
            {phoneLink ? <Link href={phoneLink}>{dispatch.courierPhone}</Link> : dispatch.courierPhone}
          </InfoItem>
        )}
        {dispatch.estimatedPickupOn && <InfoItem label="Estimated pickup">{formatDateTime(dispatch.estimatedPickupOn)}</InfoItem>}
        {dispatch.estimatedDeliveryOn && <InfoItem label="Estimated delivery">{formatDateTime(dispatch.estimatedDeliveryOn)}</InfoItem>}
        {dispatch.pickedUpOn && (
          <InfoItem label={dispatch.errorCode === 'CANCEL_AFTER_PICKUP_CONFLICT'
            ? 'Pickup evidence recorded' : 'Picked up'}>
            {formatDateTime(dispatch.pickedUpOn)}
          </InfoItem>
        )}
        {trackingLink && (
          <InfoItem label="Tracking">
            <Link href={trackingLink} target="_blank" rel="noopener noreferrer">Open Glovo tracking</Link>
          </InfoItem>
        )}
        {dispatch.errorCode && <InfoItem label="Attention code">{dispatch.errorCode}</InfoItem>}
      </Box>
    </>
  );
};

const DeliveryOrderRow = ({ order, onRefreshCourier, action, nowMs, reportBusy, courierBusy }) => {
  const dispatch = order.courierDispatch;
  const contactPhoneLink = getPhoneLink(order.contactPhone);
  const currentAction = action?.generation === dispatch?.generation ? action : null;
  const isFailed = order.deliveryStatus === 6;
  const shortOrderNumber = getShortOrderNumber(order.code, order.iref);
  const courierSummary = dispatch
    ? (dispatch.courierName || 'Courier not assigned')
    : 'No dispatch record';
  const nextAllowedOn = currentAction?.nextAllowedOn || order.courierRefresh.nextAllowedOn;
  const remaining = nextAllowedOn ? Math.max(0, Date.parse(nextAllowedOn) - nowMs) : 0;
  return (
    <Accordion disableGutters elevation={0} sx={{
      borderBottom: '1px solid rgba(15,23,42,0.08)',
      borderLeft: isFailed ? '4px solid #d32f2f' : '4px solid transparent',
      bgcolor: isFailed ? 'rgba(211,47,47,0.035)' : '#fff',
      '&:before': { display: 'none' },
      '&.Mui-expanded': { m: 0, bgcolor: isFailed ? 'rgba(211,47,47,0.055)' : 'rgba(26,35,126,0.02)' }
    }}>
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} aria-controls={`delivery-${order.iref}-content`}
        id={`delivery-${order.iref}-header`} sx={{ minHeight: 52, '&.Mui-expanded': { minHeight: 52 },
          '& .MuiAccordionSummary-content': { my: 0.75 }, '& .MuiAccordionSummary-content.Mui-expanded': { my: 0.75 } }}>
        <Box sx={{ width: '100%', pr: 0.5, display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(170px, 1.25fr) 145px minmax(200px, 1fr)' },
          gap: { xs: 0.65, sm: 1.25 }, alignItems: 'center' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: '#1a237e', fontWeight: 900, fontSize: '0.82rem' }}>
              Order {shortOrderNumber}
              <Typography component="span" sx={{ ml: 0.65, color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>
                · #{order.iref}
              </Typography>
            </Typography>
            <Typography noWrap title={order.location.name} sx={{ fontSize: '0.78rem' }}>{order.location.name}</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.76rem', fontVariantNumeric: 'tabular-nums' }}>{formatDateTime(order.scheduledOn)}</Typography>
          <Stack direction="row" spacing={0} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0, gap: 0.65 }}>
            <Chip size="small" color={getDeliveryStatusColor(order.deliveryStatus)} variant={isFailed ? 'filled' : 'outlined'}
              label={isFailed ? 'Delivery failed' : (order.deliveryStatusLabel || 'Unknown')} sx={{ height: 23, fontSize: '0.68rem' }} />
            <Typography sx={{ color: '#64748b', fontSize: '0.68rem' }}>
              {courierSummary}
            </Typography>
          </Stack>
        </Box>
      </AccordionSummary>
      <AccordionDetails id={`delivery-${order.iref}-content`} sx={{ pt: 0, pb: 1.5 }}>
        {isFailed && <Alert severity="error" sx={{ mb: 1.25 }}>Delivery failed. Review the stored courier dispatch details below.</Alert>}
        <Typography sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.78rem', mb: 0.75 }}>Order information</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.1 }}>
          <InfoItem label="Order code">{order.code || '—'}</InfoItem>
          <InfoItem label="Created">{formatDateTime(order.createdOn)}</InfoItem>
          <InfoItem label="Scheduled">{formatDateTime(order.scheduledOn)}</InfoItem>
          <InfoItem label="Promised date">{order.promisedDate}</InfoItem>
          <InfoItem label="Order status">{order.orderStatusLabel || order.orderStatus}</InfoItem>
          <InfoItem label="Delivery progress">{order.deliveryStatusLabel || order.deliveryStatus}</InfoItem>
          <InfoItem label="Lines">{order.numberOfLines}</InfoItem>
          <InfoItem label="Total">{Number(order.totalAmount).toFixed(2)}</InfoItem>
          <InfoItem label="Delivery contact">{order.contactName || '—'}</InfoItem>
          <InfoItem label="Delivery phone">
            {contactPhoneLink ? <Link href={contactPhoneLink}>{order.contactPhone}</Link> : (order.contactPhone || '—')}
          </InfoItem>
        </Box>
        <Typography sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.78rem', mt: 1.5 }}>Courier dispatch</Typography>
        <CourierDetails dispatch={dispatch} />
        {order.courierRefresh.eligible && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1.25, gap: 0.5 }}>
            <Button size="small" variant="outlined" startIcon={currentAction?.loading ? <CircularProgress size={14} /> : <RefreshRoundedIcon />}
              disabled={reportBusy || courierBusy || remaining > 0}
              onClick={() => onRefreshCourier(order)}>
              Refresh courier details
            </Button>
            {remaining > 0 && <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums' }}>
              Available in {formatRemaining(remaining)}
            </Typography>}
          </Stack>
        )}
        {currentAction?.error && <Alert severity="warning" sx={{ mt: 1 }}>{currentAction.error}</Alert>}
      </AccordionDetails>
    </Accordion>
  );
};

const navigationButtonSx = { width: 34, height: 34, p: 0.5, flex: '0 0 auto' };

export default function DeliveryOrders({ client, initialDate }) {
  const today = getBusinessDate();
  const [selectedDate, setSelectedDate] = React.useState(
    isIsoDate(initialDate) && initialDate >= '2000-01-01' && initialDate <= today ? initialDate : today
  );
  const [locations, setLocations] = React.useState([]);
  const [selectedLocation, setSelectedLocation] = React.useState('0');
  const [selectedStatus, setSelectedStatus] = React.useState('all');
  const [locationsState, setLocationsState] = React.useState({ loading: true, error: '' });
  const [locationsRetry, setLocationsRetry] = React.useState(0);
  const [reportRefresh, setReportRefresh] = React.useState(0);
  const [reportState, setReportState] = React.useState({
    loading: false, initialized: false, error: '', rows: [], background: false, stale: false, lastUpdated: null
  });
  const [nowMs, setNowMs] = React.useState(Date.now());
  const [courierActions, setCourierActions] = React.useState({});
  const [courierBusy, setCourierBusy] = React.useState(false);
  const reportQueryRef = React.useRef({ client: null, key: '' });
  const lastReportRequestAt = React.useRef(0);
  const lastBusinessDate = React.useRef(today);
  const courierRequestRef = React.useRef(null);
  const courierTokenRef = React.useRef(0);

  React.useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  React.useEffect(() => {
    setCourierActions({});
    setCourierBusy(false);
    return () => {
      courierTokenRef.current += 1;
      courierRequestRef.current?.abort?.();
      courierRequestRef.current = null;
    };
  }, [client, selectedDate, selectedLocation, selectedStatus]);

  React.useEffect(() => {
    let active = true;
    setLocations([]);
    setSelectedLocation('0');
    reportQueryRef.current = { client: null, key: '' };
    setReportState({
      loading: false, initialized: false, error: '', rows: [], background: false, stale: false, lastUpdated: null
    });
    setLocationsState({ loading: true, error: '' });
    const request = SimpleBiReportsApi.locationsListAsync(client);
    const timer = setTimeout(() => {
      if (active) {
        active = false;
        request?.abort?.();
        setLocationsState({ loading: false, error: 'Locations timed out. Retry to load them again.' });
      }
    }, 30000);
    Promise.resolve(request).then(data => {
      const rows = normalizeLocations(data);
      if (active) {
        setLocations(rows);
        setSelectedLocation(current => {
          if (rows.length === 1) return rows[0].iref;
          return current !== '0' && rows.some(row => row.iref === current) ? current : '0';
        });
      }
    }).catch(error => {
      if (active) setLocationsState({ loading: false, error: errorMessage(error, 'Locations') });
    }).finally(() => {
      clearTimeout(timer);
      if (active) setLocationsState(current => ({ ...current, loading: false }));
    });
    return () => {
      active = false;
      clearTimeout(timer);
      request?.abort?.();
    };
  }, [client, locationsRetry]);

  React.useEffect(() => {
    if (locationsState.loading || locationsState.error || !locations.length) return undefined;
    let active = true;
    const query = { date: selectedDate };
    if (selectedLocation !== '0') query.location = selectedLocation;
    if (selectedStatus !== 'all') query.deliveryStatus = selectedStatus;
    const key = JSON.stringify(query);
    const sameQuery = reportQueryRef.current.client === client && reportQueryRef.current.key === key;
    reportQueryRef.current = { client, key };
    lastReportRequestAt.current = Date.now();
    setReportState(current => ({
      ...current,
      loading: true,
      initialized: sameQuery && current.initialized,
      error: sameQuery && current.stale ? current.error : '',
      rows: sameQuery ? current.rows : [],
      background: sameQuery && current.initialized,
      stale: sameQuery && current.stale,
      lastUpdated: sameQuery ? current.lastUpdated : null
    }));
    const fail = (message, clearRows = false) => setReportState(current => {
      const stale = sameQuery && !clearRows && current.lastUpdated !== null;
      return {
        loading: false, initialized: true, error: message,
        rows: stale ? current.rows : [], background: false, stale,
        lastUpdated: stale ? current.lastUpdated : null
      };
    });
    const request = SimpleBiReportsApi.deliveryOrdersAsync(client, query);
    const timer = setTimeout(() => {
      if (active) {
        active = false;
        request?.abort?.();
        fail('Delivery orders timed out. Retry to load them again.');
      }
    }, 30000);
    Promise.resolve(request).then(data => {
      if (!validDeliveryResponse(data, selectedDate)) throw new Error('Invalid delivery orders response.');
      if (active) setReportState({
        loading: false, initialized: true, error: '', rows: data.results,
        background: false, stale: false, lastUpdated: Date.now()
      });
    }).catch(error => {
      if (active) fail(errorMessage(error),
        [400, 401, 403, 404].includes(error?.status) || error?.message === 'Invalid delivery orders response.');
    }).finally(() => clearTimeout(timer));
    return () => {
      active = false;
      clearTimeout(timer);
      request?.abort?.();
    };
  }, [client, locations, locationsState.error, locationsState.loading, reportRefresh, selectedDate, selectedLocation, selectedStatus]);

  const refreshCourier = order => {
    if (courierRequestRef.current || reportState.loading || reportState.stale) return;
    const orderId = order.iref;
    const generation = order.courierDispatch?.generation;
    const token = ++courierTokenRef.current;
    const request = SimpleBiReportsApi.refreshDeliveryCourierAsync(client, orderId, selectedDate);
    courierRequestRef.current = request;
    setCourierBusy(true);
    setCourierActions(current => ({ ...current, [orderId]: { generation, loading: true, error: '' } }));
    Promise.resolve(request).then(data => {
      if (!validDateTime(data?.nextAllowedOn)) throw new Error('Invalid courier refresh response.');
      if (courierTokenRef.current !== token) return;
      setNowMs(Date.now());
      setCourierActions(current => ({ ...current, [orderId]: {
        generation, loading: false, error: '', nextAllowedOn: data.nextAllowedOn
      } }));
      setReportRefresh(value => value + 1);
    }).catch(error => {
      if (courierTokenRef.current !== token) return;
      const response = error?.responseJSON;
      const nextAllowedOn = validDateTime(response?.nextAllowedOn) ? response.nextAllowedOn : null;
      setNowMs(Date.now());
      setCourierActions(current => ({ ...current, [orderId]: {
        generation,
        loading: false,
        error: error?.status === 429
          ? 'Courier refresh is limited. Use the timer before trying again.'
          : 'Courier details could not be refreshed. The stored report will be checked again.',
        nextAllowedOn
      } }));
      setReportRefresh(value => value + 1);
    }).finally(() => {
      if (courierTokenRef.current === token) {
        courierRequestRef.current = null;
        setCourierBusy(false);
      }
    });
  };

  React.useEffect(() => {
    if (locationsState.loading || locationsState.error || !locations.length) return undefined;
    const refreshIfDue = () => {
      if (document.visibilityState !== 'visible' || Date.now() - lastReportRequestAt.current < AUTO_REFRESH_MS) return;
      const currentBusinessDate = getBusinessDate();
      const previousBusinessDate = lastBusinessDate.current;
      lastBusinessDate.current = currentBusinessDate;
      setSelectedDate(current => current === previousBusinessDate ? currentBusinessDate : current);
      setReportRefresh(value => value + 1);
    };
    const timer = setTimeout(refreshIfDue, AUTO_REFRESH_MS);
    document.addEventListener('visibilitychange', refreshIfDue);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', refreshIfDue);
    };
  }, [client, locations, locationsState.error, locationsState.loading, reportRefresh, selectedDate, selectedLocation, selectedStatus]);

  const moveDate = days => setSelectedDate(value => shiftIsoDate(value, days));
  const locationControl = locations.length === 1 ? (
    <Box data-testid="single-delivery-location" role="group" aria-label={`Location: ${locations[0].name}`}
      sx={{ minWidth: 0, px: 1, py: 0.55, border: '1px solid rgba(15,23,42,0.16)', borderRadius: '8px' }}>
      <Typography sx={{ color: '#64748b', fontSize: '0.62rem', fontWeight: 800 }}>Location</Typography>
      <Typography noWrap title={locations[0].name} sx={{ color: '#111827', fontSize: '0.78rem' }}>{locations[0].name}</Typography>
    </Box>
  ) : (
    <TextField label="Location" select size="small" value={selectedLocation} disabled={locationsState.loading || !locations.length}
      onChange={event => setSelectedLocation(String(event.target.value))}>
      <MenuItem value="0">All assigned locations</MenuItem>
      {locations.map(row => <MenuItem key={row.iref} value={row.iref}>{row.name}</MenuItem>)}
    </TextField>
  );

  return (
    <Box role="region" aria-label="Delivery orders report" tabIndex={0} sx={{
      bgcolor: '#fff', borderRadius: 2,
      height: { xs: 'calc(100vh - 92px)', md: 'calc(100vh - 104px)' },
      '@supports (height: 100dvh)': { height: { xs: 'calc(100dvh - 92px)', md: 'calc(100dvh - 104px)' } },
      display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: { xs: 0, md: 540 }, minWidth: 0
    }}>
      <Box sx={{ p: 1.25, pb: 1, borderBottom: '1px solid rgba(15,23,42,0.08)' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: { xs: 'stretch', lg: 'center' },
          justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Box>
              <Typography sx={{ color: '#1a237e', fontWeight: 900, fontSize: '0.95rem' }}>Delivery orders</Typography>
              <Typography sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.7rem' }}>
                {locationsState.loading || !reportState.initialized
                  ? 'Loading stored delivery records…'
                  : reportState.loading
                    ? 'Updating stored delivery records…'
                    : `${reportState.rows.length} ${reportState.rows.length === 1 ? 'order' : 'orders'}`} · Promised date
              </Typography>
              {reportState.initialized && !reportState.loading && (
                <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontVariantNumeric: 'tabular-nums' }}>
                  {document.visibilityState === 'visible'
                    ? `Next stored-data refresh in ${formatRemaining(Math.min(AUTO_REFRESH_MS, AUTO_REFRESH_MS - (nowMs - lastReportRequestAt.current)))}`
                    : 'Automatic refresh paused while tab is hidden'}
                </Typography>
              )}
            </Box>
            <Tooltip title="Refresh stored data (automatic every minute while visible)"><IconButton aria-label="Refresh delivery orders" sx={navigationButtonSx}
              onClick={() => setReportRefresh(value => value + 1)}><RefreshRoundedIcon fontSize="small" /></IconButton></Tooltip>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'minmax(150px, 220px) minmax(145px, 180px)' }, gap: 0.75, minWidth: 0 }}>
            {locationControl}
            <TextField label="Delivery status" select size="small" value={selectedStatus}
              onChange={event => setSelectedStatus(String(event.target.value))}>
              <MenuItem value="all">All statuses</MenuItem>
              {DELIVERY_STATUS_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
          </Box>
        </Box>
        <Stack data-testid="delivery-date-navigation" direction="row" spacing={0.25} alignItems="center"
          sx={{ flexWrap: 'nowrap', minWidth: 0, mt: 1 }}>
          <Tooltip title="Previous day"><span><IconButton aria-label="Previous day" sx={navigationButtonSx}
            disabled={selectedDate <= '2000-01-01'} onClick={() => moveDate(-1)}><ChevronLeftRoundedIcon /></IconButton></span></Tooltip>
          <TextField label="Promised date" type="date" size="small" value={selectedDate}
            onChange={event => {
              const value = event.target.value;
              if (isIsoDate(value) && value >= '2000-01-01' && value <= today) setSelectedDate(value);
            }}
            InputLabelProps={{ shrink: true }} inputProps={{ min: '2000-01-01', max: today }}
            sx={{ width: { xs: 160, sm: 165 }, flex: '0 0 auto', '& .MuiInputBase-root': { height: 36 } }} />
          <Tooltip title="Next day"><span><IconButton aria-label="Next day" sx={navigationButtonSx}
            disabled={selectedDate >= today} onClick={() => moveDate(1)}><ChevronRightRoundedIcon /></IconButton></span></Tooltip>
          <Tooltip title="Go to today"><span><IconButton aria-label="Go to today" sx={navigationButtonSx}
            disabled={selectedDate === today} onClick={() => setSelectedDate(today)}><TodayRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
        </Stack>
      </Box>

      {reportState.loading && reportState.initialized && <LinearProgress aria-label="Loading delivery orders" />}
      {reportState.stale && <Alert severity="warning" sx={{ mx: 1, mt: 1 }}
        action={<Button color="inherit" onClick={() => setReportRefresh(value => value + 1)}>Retry</Button>}>
        {reportState.error} Showing previously loaded data from {formatDateTime(reportState.lastUpdated)} (Bucharest).
      </Alert>}
      <Box aria-busy={locationsState.loading || reportState.loading} sx={{ flex: 1, minHeight: 0, overflowY: 'auto',
        opacity: reportState.loading && reportState.initialized && !reportState.background ? 0.55 : 1,
        pointerEvents: reportState.loading && reportState.initialized && !reportState.background ? 'none' : 'auto',
        transition: 'opacity 120ms ease' }}>
        {locationsState.error ? (
          <Alert severity="error" action={<Button color="inherit" onClick={() => setLocationsRetry(value => value + 1)}>Retry</Button>} sx={{ m: 1 }}>
            {locationsState.error}
          </Alert>
        ) : locationsState.loading ? (
          <Stack role="status" direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ minHeight: 180 }}>
            <CircularProgress size={22} /><Typography sx={{ fontSize: '0.78rem' }}>Loading authorized locations…</Typography>
          </Stack>
        ) : !locations.length ? (
          <Alert severity="info" sx={{ m: 1 }}>No locations are available for your account.</Alert>
        ) : !reportState.initialized ? (
          <Stack role="status" direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ minHeight: 180 }}>
            <CircularProgress size={22} /><Typography sx={{ fontSize: '0.78rem' }}>Loading delivery orders…</Typography>
          </Stack>
        ) : reportState.error && !reportState.stale ? (
          <Alert severity="error" action={<Button color="inherit" onClick={() => setReportRefresh(value => value + 1)}>Retry</Button>} sx={{ m: 1 }}>
            {reportState.error}
          </Alert>
        ) : !reportState.rows.length ? (
          <Stack role="status" alignItems="center" justifyContent="center" sx={{ minHeight: 180, px: 2 }}>
            <Typography sx={{ color: '#64748b', fontSize: '0.82rem', textAlign: 'center' }}>
              No delivery orders match the selected date and filters.
            </Typography>
          </Stack>
        ) : reportState.rows.map(order => <DeliveryOrderRow key={order.iref} order={order}
          onRefreshCourier={refreshCourier} action={courierActions[order.iref]} nowMs={nowMs}
          reportBusy={reportState.loading || reportState.stale} courierBusy={courierBusy} />)}
      </Box>
    </Box>
  );
}
